import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  BadgeCheck,
  BriefcaseBusiness,
  ClipboardCheck,
  Cloud,
  Code,
  Container,
  Download,
  FileText,
  Lock,
  MessageCircle,
  Network,
  Server,
  ShieldCheck,
  Trophy
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const showcaseProjects = [
  {
    title: 'Production CI/CD to Kubernetes',
    tools: 'GitHub Actions, Docker, Kubernetes, container registry',
    outcome: 'Build, tag, push, and deploy an application with environment-based release controls.'
  },
  {
    title: 'Terraform Cloud Infrastructure',
    tools: 'Terraform, AWS or Azure, VPC, compute, IAM, remote state',
    outcome: 'Provision repeatable infrastructure from code and document the architecture decisions.'
  },
  {
    title: 'Monitoring and Incident Recovery',
    tools: 'Prometheus, Grafana, alerts, logs, runbooks',
    outcome: 'Detect failure signals, recover service health, and explain the root cause like an SRE.'
  },
  {
    title: 'Dockerized Multi-Service App',
    tools: 'Docker, Docker Compose, image optimization, networking',
    outcome: 'Package services consistently and prepare them for CI/CD and Kubernetes deployment.'
  }
];

const howItWorksSteps = [
  ['Learn', 'Master the concepts, commands, and production patterns behind each DevOps tool.'],
  ['Build', 'Complete hands-on labs that create portfolio evidence instead of passive notes.'],
  ['Submit', 'Upload scripts, links, screenshots, repositories, and architecture explanations.'],
  ['Review', 'Instructors and teaching assistants approve, reject, or request improvements.'],
  ['Advance', 'Approved work unlocks the next module and moves you closer to Career Launch.']
];

const certificateProof = [
  'Unique certificate ID and employer verification link',
  'Published skills, project evidence, and readiness score',
  'Instructor-reviewed portfolio and Career Launch report',
  'Shareable proof for LinkedIn, resumes, and interviews'
];

const successStories = [
  {
    name: 'Systems Administrator',
    result: 'Built cloud automation confidence',
    story: 'Moved from manual server work into Terraform, CI/CD, and Kubernetes project delivery.'
  },
  {
    name: 'Software Developer',
    result: 'Added production delivery skills',
    story: 'Turned application knowledge into deployable containers, pipelines, and monitoring workflows.'
  },
  {
    name: 'Cloud Support Engineer',
    result: 'Prepared for DevOps interviews',
    story: 'Used reviewed projects and Career Launch practice to explain real troubleshooting scenarios.'
  }
];

const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [showAuth, setShowAuth] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Check URL params for auth modal
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('auth') === 'true') {
      setShowAuth(true);
      // Clean up URL
      navigate('/', { replace: true });
    }
  }, [location, navigate]);

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: 'var(--bg-default)' }}>
      {/* Corporate Professional Navbar */}
      <Navbar onSignInClick={() => setShowAuth(true)} />
      
      {/* Add top padding to account for fixed navbar + top bar (42px + 80px) */}
      <div className="pt-[122px]">
        {/* Noise texture overlay */}
        <div className="absolute inset-0 noise-bg opacity-30"></div>
        
        {/* Grid background */}
        <div className="absolute inset-0" style={{
          backgroundImage: theme === 'dark' 
            ? 'linear-gradient(rgba(0, 229, 255, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 229, 255, 0.03) 1px, transparent 1px)'
            : 'linear-gradient(rgba(14, 165, 233, 0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(14, 165, 233, 0.08) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>

      {/* Hero Section */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-24 pb-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="uppercase text-xs tracking-widest text-[#00E5FF] mb-6 font-medium">
              9-MONTH INTENSIVE PROGRAM
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-none tracking-tight mb-6 text-[#F8FAFC]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Master DevOps
              <br />
              <span className="text-[#00E5FF]">From Zero to Hero</span>
            </h1>
            <p className="text-xl text-[#94A3B8] leading-relaxed mb-8 max-w-2xl">
              Comprehensive training covering Linux, Cloud, Containers, Kubernetes, CI/CD, and Observability. 
              Vetted by industry experts every step of the way.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                data-testid="hero-get-started-btn"
                onClick={() => setShowAuth(true)}
                className="bg-[#00E5FF] text-black hover:bg-[#33EBFF] font-semibold px-8 py-6 text-lg rounded-md"
              >
                Get Started
              </Button>
              <Button 
                asChild
                data-testid="hero-download-syllabus-btn"
                variant="outline"
                className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B] hover:border-[#00E5FF] px-8 py-6 text-lg rounded-md"
              >
                <a href="/peabsmart-devops-syllabus.pdf" download>
                  <Download className="w-5 h-5 mr-2" />
                  Download Syllabus
                </a>
              </Button>
            </div>
          </div>
          
          <div className="md:col-span-5">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-radial from-[#00E5FF]/20 to-transparent blur-3xl"></div>
              <img 
                src="https://images.unsplash.com/photo-1691435828932-911a7801adfb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxzZXJ2ZXIlMjByb29tJTIwYmx1ZSUyMGxpZ2h0aW5nJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NzE5MzIxMjF8MA&ixlib=rb-4.1.0&q=85"
                alt="Server infrastructure"
                className="relative rounded-lg shadow-2xl border border-[#1E293B]"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Curriculum Grid */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pb-32">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-[#F8FAFC]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            9-Module Curriculum
          </h2>
          <p className="text-xl text-[#94A3B8]">
            Progress through vetted assignments to unlock each level
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { 
              icon: <Code />, 
              title: 'Linux Fundamentals', 
              desc: 'Command line mastery',
              careers: ['Junior DevOps Engineer', 'Cloud Support Engineer', 'Linux System Administrator'],
              projects: [
                'Configure and secure a production Linux server',
                'Create users, manage permissions, and harden SSH access',
                'Set up Nginx or Apache web server',
                'Monitor system resources and troubleshoot performance issues'
              ],
              relevance: 'Linux powers over 90% of cloud infrastructure. You\'ll gain the same Linux administration skills used to manage EC2 instances, cloud VMs, and container hosts in production.'
            },
            { 
              icon: <Code />, 
              title: 'Bash Scripting', 
              desc: 'Automation & scripting',
              careers: ['Automation Engineer', 'DevOps Intern / Junior DevOps', 'Site Reliability Engineer (SRE) (Entry-Level)'],
              projects: [
                'Build a backup automation script',
                'Write deployment automation scripts',
                'Automate log cleanup and monitoring tasks',
                'Create user provisioning automation scripts'
              ],
              relevance: 'Automation is the heart of DevOps. You\'ll automate tasks that DevOps engineers handle daily — reducing manual errors and improving system reliability.'
            },
            { 
              icon: <Code />, 
              title: 'Git & GitHub', 
              desc: 'Version control workflows',
              careers: ['DevOps Engineer', 'Cloud Engineer', 'Platform Engineer'],
              projects: [
                'Implement Git branching strategy (GitFlow)',
                'Manage pull requests and code reviews',
                'Resolve merge conflicts in collaborative projects',
                'Host and manage production repositories on GitHub'
              ],
              relevance: 'Version control is foundational for CI/CD and infrastructure automation. You\'ll operate like a real engineering team using Git workflows and collaboration best practices.'
            },
            { 
              icon: <Cloud />, 
              title: 'Cloud Basics', 
              desc: 'AWS, Azure, GCP',
              careers: ['Cloud Engineer', 'Cloud Support Associate', 'DevOps Engineer'],
              projects: [
                'Launch and configure EC2 / VM instances',
                'Set up IAM roles and policies',
                'Configure VPC networking and security groups',
                'Deploy a scalable web application in the cloud'
              ],
              relevance: 'Cloud is where modern infrastructure lives. You\'ll understand how enterprise-grade applications are deployed, secured, and scaled on major cloud platforms.'
            },
            { 
              icon: <Server />, 
              title: 'Infrastructure as Code', 
              desc: 'Terraform & Pulumi',
              careers: ['DevOps Engineer', 'Cloud Infrastructure Engineer', 'Platform Engineer'],
              projects: [
                'Provision full AWS/Azure infrastructure using Terraform',
                'Create reusable infrastructure modules',
                'Automate VPC, EC2, RDS provisioning',
                'Manage remote state and environment separation (dev/staging/prod)'
              ],
              relevance: 'Infrastructure automation separates beginners from professionals. You\'ll deploy production infrastructure using code — just like modern DevOps teams.'
            },
            { 
              icon: <Container />, 
              title: 'Containerization', 
              desc: 'Docker mastery',
              careers: ['Cloud-Native Engineer', 'DevOps Engineer', 'Backend Engineer (Containerized Systems)'],
              projects: [
                'Containerize a multi-tier web application',
                'Build optimized multi-stage Docker images',
                'Create Docker Compose setups for microservices',
                'Push and manage images in container registries'
              ],
              relevance: 'Containers are the standard packaging unit in modern software delivery. You\'ll build production-ready containers used in Kubernetes and CI/CD systems.'
            },
            { 
              icon: <Network />, 
              title: 'Kubernetes', 
              desc: 'Container orchestration',
              careers: ['Kubernetes Administrator', 'DevOps Engineer', 'Site Reliability Engineer'],
              projects: [
                'Deploy applications to a Kubernetes cluster',
                'Configure services, ingress, config maps, and secrets',
                'Implement horizontal pod autoscaling',
                'Perform rolling updates and zero-downtime deployments'
              ],
              relevance: 'Kubernetes is the backbone of cloud-native infrastructure. You\'ll manage scalable production systems similar to those used by tech companies worldwide.'
            },
            { 
              icon: <Server />, 
              title: 'CI/CD Pipelines', 
              desc: 'Automated deployments',
              careers: ['DevOps Engineer', 'Release Engineer', 'Automation Engineer'],
              projects: [
                'Build a full CI/CD pipeline using GitHub Actions',
                'Automate Docker image builds and push to registry',
                'Deploy automatically to Kubernetes',
                'Implement environment-based deployments (dev/stage/prod)'
              ],
              relevance: 'CI/CD is where development meets operations. You\'ll automate real-world deployment pipelines used in enterprise environments.'
            },
            { 
              icon: <Network />, 
              title: 'Observability', 
              desc: 'Monitoring & logging',
              careers: ['Site Reliability Engineer (SRE)', 'Production Support Engineer', 'DevOps Engineer'],
              projects: [
                'Set up Prometheus monitoring for Kubernetes',
                'Create Grafana dashboards for system metrics',
                'Configure alerting rules',
                'Diagnose performance bottlenecks'
              ],
              relevance: 'Monitoring separates reactive teams from reliable systems. You\'ll gain hands-on monitoring experience essential for maintaining uptime and reliability in production systems.'
            }
          ].map((module, idx) => (
            <div 
              key={idx}
              data-testid={`module-card-${idx}`}
              onClick={() => setSelectedModule(module)}
              className="bg-[#151923] border border-[#1E293B] rounded-md p-8 hover:border-[#00E5FF] transition-all group cursor-pointer"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-md bg-[#00E5FF]/10 flex items-center justify-center text-[#00E5FF] group-hover:bg-[#00E5FF]/20 transition-colors">
                  {React.cloneElement(module.icon, { className: 'w-6 h-6' })}
                </div>
                <div className="font-mono text-sm text-[#64748B]">Module {idx + 1}</div>
              </div>
              <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {module.title}
              </h3>
              <p className="text-[#94A3B8] mb-3">{module.desc}</p>
              <div className="text-sm text-[#00E5FF] group-hover:text-[#33EBFF]">
                Click to learn more →
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Syllabus Download */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-[#151923] border border-[#1E293B] rounded-md p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-md bg-[#00E5FF]/10 text-[#00E5FF] flex items-center justify-center">
                <FileText className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-3xl font-bold mb-3 text-[#F8FAFC]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Download the full Peabsmart DevOps syllabus
                </h2>
                <p className="text-[#94A3B8] leading-relaxed mb-5">
                  Review the full 9-module roadmap, weekly pacing, project deliverables, assessment model, and Career Launch outcomes before you enroll.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-[#F8FAFC]">
                  {['Module outcomes and tools', 'Hands-on project deliverables', 'Instructor review checkpoints', 'Career Launch readiness path'].map((item) => (
                    <div key={item} className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-[#10B981]" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-[#0B0E14] border border-[#1E293B] rounded-md p-8 flex flex-col justify-between">
            <div>
              <div className="text-[#00E5FF] text-sm font-semibold mb-2">PROGRAM GUIDE</div>
              <div className="text-4xl font-bold text-[#F8FAFC] mb-2">PDF</div>
              <p className="text-[#94A3B8] mb-6">A concise syllabus for learners, sponsors, and training managers.</p>
            </div>
            <Button asChild className="bg-[#00E5FF] text-black hover:bg-[#33EBFF] font-semibold rounded-md">
              <a href="/peabsmart-devops-syllabus.pdf" download data-testid="syllabus-download-link">
                <Download className="w-4 h-4 mr-2" />
                Download Syllabus
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Project Showcase */}
      <section className="relative z-10 bg-[#0B0E14] border-y border-[#1E293B] py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div>
              <div className="uppercase text-xs tracking-widest text-[#00E5FF] mb-4 font-medium">Portfolio Projects</div>
              <h2 className="text-4xl md:text-5xl font-bold mb-4 text-[#F8FAFC]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Build proof recruiters can inspect
              </h2>
              <p className="text-xl text-[#94A3B8] max-w-3xl">
                Each project is designed to look like a real workplace task, with repositories, screenshots, commands, architecture notes, and instructor feedback.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {showcaseProjects.map((project) => (
              <div key={project.title} className="bg-[#151923] border border-[#1E293B] rounded-md p-6 hover:border-[#00E5FF] transition-colors">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-md bg-[#6366F1]/20 text-[#818CF8] flex items-center justify-center">
                    <ClipboardCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#F8FAFC] mb-2">{project.title}</h3>
                    <div className="text-sm text-[#00E5FF] mb-3">{project.tools}</div>
                    <p className="text-[#94A3B8] leading-relaxed">{project.outcome}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-14">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-[#F8FAFC]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            How Peabsmart works
          </h2>
          <p className="text-xl text-[#94A3B8] max-w-3xl mx-auto">
            A clear progression system keeps learners moving from concepts to reviewed, job-ready evidence.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {howItWorksSteps.map(([title, description], idx) => (
            <div key={title} className="bg-[#151923] border border-[#1E293B] rounded-md p-5">
              <div className="w-10 h-10 rounded-md bg-[#00E5FF] text-black flex items-center justify-center font-bold mb-4">
                {idx + 1}
              </div>
              <h3 className="text-lg font-bold text-[#F8FAFC] mb-2">{title}</h3>
              <p className="text-sm text-[#94A3B8] leading-relaxed">{description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Certificate and Employer Proof */}
      <section className="relative z-10 bg-[#151923] border-y border-[#1E293B] py-24">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="uppercase text-xs tracking-widest text-[#00E5FF] mb-4 font-medium">Verified Outcome</div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[#F8FAFC]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              A certificate backed by reviewed work
            </h2>
            <p className="text-xl text-[#94A3B8] leading-relaxed mb-8">
              Peabsmart can turn final project evidence into an employer-ready profile that shows what a graduate actually built and passed.
            </p>
            <div className="space-y-4">
              {certificateProof.map((item) => (
                <div key={item} className="flex items-start gap-3 text-[#F8FAFC]">
                  <BadgeCheck className="w-5 h-5 text-[#10B981] mt-0.5" />
                  {item}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-[#0B0E14] border border-[#334155] rounded-md p-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <div className="text-sm text-[#94A3B8]">Peabsmart Verified</div>
                <div className="text-2xl font-bold text-[#F8FAFC]">DevOps Practitioner</div>
              </div>
              <Trophy className="w-12 h-12 text-[#F59E0B]" />
            </div>
            <div className="space-y-4">
              {['Cloud Infrastructure', 'CI/CD Delivery', 'Kubernetes Operations', 'Monitoring and Recovery'].map((skill) => (
                <div key={skill}>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-[#94A3B8]">{skill}</span>
                    <span className="text-[#00E5FF]">Verified</span>
                  </div>
                  <div className="h-2 rounded-full bg-[#1E293B] overflow-hidden">
                    <div className="h-full bg-[#00E5FF]" style={{ width: '92%' }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Student Success */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-14">
          <div className="uppercase text-xs tracking-widest text-[#00E5FF] mb-4 font-medium">Success Stories</div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-[#F8FAFC]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Show the transformation, not just the curriculum
          </h2>
          <p className="text-xl text-[#94A3B8] max-w-3xl mx-auto">
            These story formats can become real alumni testimonials as graduates complete Career Launch.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {successStories.map((story) => (
            <div key={story.name} className="bg-[#151923] border border-[#1E293B] rounded-md p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-full bg-[#00E5FF]/10 text-[#00E5FF] flex items-center justify-center">
                  <BriefcaseBusiness className="w-6 h-6" />
                </div>
                <div>
                  <div className="font-bold text-[#F8FAFC]">{story.name}</div>
                  <div className="text-sm text-[#10B981]">{story.result}</div>
                </div>
              </div>
              <p className="text-[#94A3B8] leading-relaxed mb-5">{story.story}</p>
              <div className="flex items-center gap-2 text-sm text-[#00E5FF]">
                <MessageCircle className="w-4 h-4" />
                Add graduate quote and LinkedIn proof
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Why Choose Section */}
      <div className="relative z-10 bg-[#151923] border-y border-[#1E293B] py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-[#F8FAFC]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Why Choose Our DevOps Program
            </h2>
            <p className="text-xl text-[#94A3B8]">
              Not just another online course — a complete career transformation system
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-[#0B0E14] border border-[#1E293B] rounded-md p-8">
              <div className="text-[#00E5FF] font-bold text-xl mb-3">1. Structured Like a Real Engineering Career</div>
              <p className="text-[#94A3B8] mb-4">This isn't random tutorials stitched together.</p>
              <p className="text-[#F8FAFC] mb-4">Our 9-month roadmap mirrors how DevOps engineers grow in real companies:</p>
              <div className="space-y-2 text-[#94A3B8]">
                <p>• Linux → Automation → Version Control</p>
                <p>• Cloud Infrastructure → Infrastructure as Code</p>
                <p>• Containers → Kubernetes → CI/CD</p>
                <p>• Observability & Production Monitoring</p>
              </div>
              <p className="text-[#F8FAFC] mt-4 font-medium">You don't just learn tools. You learn how they connect in production systems.</p>
            </div>

            <div className="bg-[#0B0E14] border border-[#1E293B] rounded-md p-8">
              <div className="text-[#00E5FF] font-bold text-xl mb-3">2. Gated Progression — Earn Your Advancement</div>
              <p className="text-[#94A3B8] mb-4">In real tech jobs, you don't get promoted for watching videos. You advance by proving your skills.</p>
              <p className="text-[#F8FAFC] mb-3">Our platform requires:</p>
              <div className="space-y-2 text-[#94A3B8] mb-4">
                <p>• Assignment submission</p>
                <p>• Code review & evaluation</p>
                <p>• Technical feedback from instructors</p>
                <p>• Approval before unlocking the next module</p>
              </div>
              <p className="text-[#F8FAFC] font-medium">This ensures every graduate meets real competency standards.</p>
            </div>

            <div className="bg-[#0B0E14] border border-[#1E293B] rounded-md p-8">
              <div className="text-[#00E5FF] font-bold text-xl mb-3">3. Real-World Projects — Not Toy Examples</div>
              <p className="text-[#F8FAFC] mb-3">You will:</p>
              <div className="space-y-2 text-[#94A3B8] mb-4">
                <p>• Deploy cloud infrastructure using Terraform</p>
                <p>• Containerize applications with Docker</p>
                <p>• Build Kubernetes clusters</p>
                <p>• Implement CI/CD pipelines</p>
                <p>• Configure monitoring with Prometheus & Grafana</p>
                <p>• Automate infrastructure deployments end-to-end</p>
              </div>
              <p className="text-[#F8FAFC] font-medium">By graduation, you'll have a portfolio that reflects real DevOps workflows.</p>
            </div>

            <div className="bg-[#0B0E14] border border-[#1E293B] rounded-md p-8">
              <div className="text-[#00E5FF] font-bold text-xl mb-3">4. Designed for Industry Readiness</div>
              <p className="text-[#94A3B8] mb-4">We focus on what companies actually use:</p>
              <div className="space-y-2 text-[#94A3B8] mb-4">
                <p>• AWS, Azure, GCP fundamentals</p>
                <p>• Terraform & Pulumi</p>
                <p>• Docker & Kubernetes</p>
                <p>• GitHub Actions, Jenkins, GitLab CI</p>
                <p>• Production monitoring systems</p>
              </div>
              <p className="text-[#F8FAFC] font-medium">You graduate understanding how modern cloud-native systems operate.</p>
            </div>

            <div className="bg-[#0B0E14] border border-[#1E293B] rounded-md p-8">
              <div className="text-[#00E5FF] font-bold text-xl mb-3">5. Mentorship & Technical Vetting</div>
              <p className="text-[#94A3B8] mb-4">Assignments aren't auto-graded.</p>
              <p className="text-[#F8FAFC] mb-3">They are reviewed by instructors and teaching assistants who:</p>
              <div className="space-y-2 text-[#94A3B8] mb-4">
                <p>• Provide structured technical feedback</p>
                <p>• Suggest improvements</p>
                <p>• Validate readiness before progression</p>
              </div>
              <p className="text-[#F8FAFC] font-medium">This simulates real engineering review processes.</p>
            </div>

            <div className="bg-[#0B0E14] border border-[#1E293B] rounded-md p-8">
              <div className="text-[#00E5FF] font-bold text-xl mb-3">6. Cohort-Based Learning</div>
              <p className="text-[#94A3B8] mb-4">You join a focused group of learners on the same journey:</p>
              <div className="space-y-2 text-[#94A3B8]">
                <p>• Structured milestones</p>
                <p>• Progress tracking</p>
                <p>• Clear 9-month transformation roadmap</p>
                <p>• Accountability & consistency</p>
              </div>
            </div>

            <div className="bg-[#0B0E14] border border-[#1E293B] rounded-md p-8 md:col-span-2">
              <div className="text-[#00E5FF] font-bold text-xl mb-3">7. Built for Career Transformation</div>
              <p className="text-[#F8FAFC] mb-4">By the end of the program, you will be able to:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[#94A3B8] mb-4">
                <p>• Design cloud infrastructure from scratch</p>
                <p>• Automate deployments using CI/CD</p>
                <p>• Manage Kubernetes clusters</p>
                <p>• Monitor production systems</p>
                <p>• Troubleshoot real-world issues</p>
                <p>• Operate like a DevOps Engineer on Day 1</p>
              </div>
              <p className="text-[#F8FAFC] font-medium text-lg">This isn't just learning DevOps. This is becoming one.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Final Career Transformation */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-24">
        <div className="bg-gradient-to-br from-[#00E5FF]/10 to-[#6366F1]/10 border border-[#00E5FF]/30 rounded-lg p-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-center text-[#F8FAFC]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Final Career Transformation Outcome
          </h2>
          <p className="text-xl text-[#94A3B8] text-center mb-8">
            By the end of this 9-month program, students will be able to:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'Design and deploy scalable cloud infrastructure',
              'Automate infrastructure provisioning',
              'Build and manage CI/CD pipelines',
              'Deploy containerized applications to Kubernetes',
              'Monitor and maintain production systems',
              'Operate like a professional DevOps Engineer'
            ].map((outcome, idx) => (
              <div key={idx} className="bg-[#151923] border border-[#1E293B] rounded-md p-6">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#00E5FF] flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-black" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-[#F8FAFC] font-medium">{outcome}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-[#F8FAFC]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Invest in Your DevOps Career
          </h2>
          <p className="text-xl text-[#94A3B8]">
            9-month comprehensive program with hands-on projects and mentorship
          </p>
        </div>

        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* One-Time Payment */}
          <div className="bg-gradient-to-br from-[#00E5FF]/20 to-[#6366F1]/20 border-2 border-[#00E5FF] rounded-lg p-8 relative">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#00E5FF] text-black px-4 py-1 rounded-full text-sm font-bold">
              BEST VALUE
            </div>
            <div className="text-sm font-medium text-[#00E5FF] mb-2">ONE-TIME PAYMENT</div>
            <h3 className="text-3xl font-bold mb-4 text-[#F8FAFC]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Full Payment
            </h3>
            <div className="mb-6">
              <span className="text-5xl font-bold text-[#F8FAFC]">$1,100</span>
              <span className="text-[#94A3B8] block mt-2">Pay once, lifetime access</span>
            </div>
            <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-md p-4 mb-6">
              <div className="text-[#10B981] font-semibold text-sm">Save $250 with one-time payment</div>
            </div>
            <div className="space-y-3 mb-8">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-[#00E5FF] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-[#F8FAFC] font-medium">Full access to all 9 modules</span>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-[#00E5FF] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-[#F8FAFC]">Live weekly group sessions</span>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-[#00E5FF] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-[#F8FAFC]">Priority code review (48hr)</span>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-[#00E5FF] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-[#F8FAFC]">Private community access</span>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-[#00E5FF] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-[#F8FAFC]">1-on-1 career guidance</span>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-[#00E5FF] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-[#F8FAFC]">Job search & resume support</span>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-[#00E5FF] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-[#F8FAFC]">Certificate of completion</span>
              </div>
            </div>
            <Button 
              data-testid="enroll-onetime-btn"
              onClick={() => setShowAuth(true)}
              className="w-full bg-[#00E5FF] text-black hover:bg-[#33EBFF] font-semibold py-6 rounded-md"
            >
              Enroll with One-Time Payment
            </Button>
          </div>

          {/* Installment Payment */}
          <div className="bg-[#151923] border border-[#1E293B] rounded-lg p-8">
            <div className="text-sm font-medium text-[#94A3B8] mb-2">PAYMENT PLAN</div>
            <h3 className="text-3xl font-bold mb-4 text-[#F8FAFC]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              3 Installments
            </h3>
            <div className="mb-6">
              <span className="text-5xl font-bold text-[#F8FAFC]">$1,350</span>
              <span className="text-[#94A3B8] block mt-2">$450 × 3 monthly payments</span>
            </div>
            <div className="bg-[#1E293B] border border-[#334155] rounded-md p-4 mb-6">
              <div className="text-[#94A3B8] text-sm">Flexible payment option</div>
            </div>
            <div className="space-y-3 mb-8">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-[#F8FAFC] font-medium">Full access to all 9 modules</span>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-[#F8FAFC]">Live weekly group sessions</span>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-[#F8FAFC]">Priority code review (48hr)</span>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-[#F8FAFC]">Private community access</span>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-[#F8FAFC]">1-on-1 career guidance</span>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-[#F8FAFC]">Job search & resume support</span>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-[#F8FAFC]">Certificate of completion</span>
              </div>
            </div>
            <Button 
              data-testid="enroll-installment-btn"
              onClick={() => setShowAuth(true)}
              className="w-full bg-[#334155] text-[#F8FAFC] hover:bg-[#475569] font-semibold py-6 rounded-md"
            >
              Enroll with Payment Plan
            </Button>
          </div>
        </div>

        <p className="text-center text-[#94A3B8] mt-12">
          All plans include the same comprehensive features and benefits
        </p>
      </div>

      {/* Auth Modal */}
      {showAuth && (
        <AuthModal onClose={() => setShowAuth(false)} />
      )}

      {/* Module Details Modal */}
      {selectedModule && (
        <ModuleDetailsModal module={selectedModule} onClose={() => setSelectedModule(null)} />
      )}
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

const ModuleDetailsModal = ({ module, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
      <div 
        data-testid="module-details-modal"
        className="bg-[#151923] border border-[#1E293B] rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[#151923] border-b border-[#1E293B] p-6 flex justify-between items-start z-10">
          <div>
            <h2 className="text-3xl font-bold mb-2 text-[#F8FAFC]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {module.title}
            </h2>
            <p className="text-[#94A3B8]">{module.desc}</p>
          </div>
          <button
            onClick={onClose}
            className="text-[#94A3B8] hover:text-[#F8FAFC] p-2"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-8">
          {/* Career Outcomes */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-2xl font-bold text-[#F8FAFC]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Career Outcome
              </h3>
            </div>
            <p className="text-[#F8FAFC] mb-4 text-lg">Prepares you for:</p>
            <div className="space-y-2">
              {module.careers.map((career, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-[#0B0E14] border border-[#1E293B] rounded-md p-4">
                  <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2 flex-shrink-0"></div>
                  <span className="text-[#F8FAFC] font-medium">{career}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Real-World Projects */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-2xl font-bold text-[#F8FAFC]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Real-World Projects
              </h3>
            </div>
            <div className="space-y-3">
              {module.projects.map((project, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-md bg-[#6366F1]/20 flex items-center justify-center text-[#6366F1] font-mono text-sm flex-shrink-0 mt-0.5">
                    {idx + 1}
                  </div>
                  <span className="text-[#94A3B8]">{project}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Industry Relevance */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-2xl font-bold text-[#F8FAFC]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Industry Relevance
              </h3>
            </div>
            <div className="bg-[#00E5FF]/10 border border-[#00E5FF]/30 rounded-md p-6">
              <p className="text-[#F8FAFC] leading-relaxed">{module.relevance}</p>
            </div>
          </div>

          {/* CTA */}
          <div className="pt-4 border-t border-[#1E293B]">
            <button
              onClick={onClose}
              className="w-full bg-[#00E5FF] text-black hover:bg-[#33EBFF] font-semibold py-4 rounded-md transition-colors"
            >
              Got it! Let's Get Started
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AuthModal = ({ onClose }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const { login, signup, resendVerificationEmail } = useAuth();
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(null);
  const [showVerificationNotice, setShowVerificationNotice] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');

  // Import validation helpers
  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push('At least 8 characters');
    if (!/[A-Z]/.test(password)) errors.push('One uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('One lowercase letter');
    if (!/[0-9]/.test(password)) errors.push('One number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) errors.push('One special character');
    return { isValid: errors.length === 0, errors };
  };

  const getPasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score += 1;
    
    if (score <= 2) return { level: 'Weak', color: '#EF4444', width: '33%' };
    if (score <= 4) return { level: 'Medium', color: '#F59E0B', width: '66%' };
    return { level: 'Strong', color: '#10B981', width: '100%' };
  };

  const handlePasswordChange = (e) => {
    const password = e.target.value;
    setFormData({ ...formData, password });
    if (!isLogin && password) {
      setPasswordStrength(getPasswordStrength(password));
    } else {
      setPasswordStrength(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        if (result.needsVerification) {
          toast.warning('Please verify your email to access all features.');
        }
        toast.success('Login successful!');
        onClose();
      } else {
        // Validate password before signup
        const validation = validatePassword(formData.password);
        if (!validation.isValid) {
          toast.error('Password requirements not met: ' + validation.errors[0]);
          setLoading(false);
          return;
        }
        
        const result = await signup(formData.email, formData.password, formData.name);
        if (result.verificationEmailSent) {
          setVerificationEmail(formData.email);
          setShowVerificationNotice(true);
        } else {
          toast.success('Account created successfully!');
          onClose();
        }
      }
    } catch (error) {
      toast.error(error.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      await resendVerificationEmail();
      toast.success('Verification email sent!');
    } catch (error) {
      toast.error('Failed to resend verification email');
    }
  };

  // Show verification notice after signup
  if (showVerificationNotice) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={onClose}>
        <div 
          className="bg-[#151923] border border-[#1E293B] rounded-lg p-8 max-w-md w-full mx-4" 
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-center">
            <div className="w-16 h-16 bg-[#10B981]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-[#10B981]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-[#F8FAFC] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Verify Your Email
            </h2>
            <p className="text-[#94A3B8] mb-6">
              We've sent a verification link to<br />
              <span className="text-[#00E5FF] font-medium">{verificationEmail}</span>
            </p>
            <p className="text-[#64748B] text-sm mb-6">
              Please check your inbox and click the verification link to complete your registration.
            </p>
            <div className="space-y-3">
              <Button 
                onClick={onClose}
                className="w-full bg-[#00E5FF] text-black hover:bg-[#33EBFF] font-semibold py-4 rounded-md"
              >
                Got it
              </Button>
              <button
                onClick={handleResendVerification}
                className="text-[#94A3B8] hover:text-[#00E5FF] text-sm"
              >
                Didn't receive the email? Resend
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div 
        data-testid="auth-modal"
        className="bg-[#151923] border border-[#1E293B] rounded-lg p-8 max-w-md w-full mx-4" 
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-3xl font-bold mb-6 text-[#F8FAFC]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {isLogin ? 'Sign In' : 'Create Account'}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div>
              <Label htmlFor="name" className="text-[#F8FAFC]">Name</Label>
              <Input
                data-testid="auth-name-input"
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-[#0B0E14] border-[#1E293B] text-[#F8FAFC] mt-2"
                required={!isLogin}
              />
            </div>
          )}
          
          <div>
            <Label htmlFor="email" className="text-[#F8FAFC]">Email</Label>
            <Input
              data-testid="auth-email-input"
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="bg-[#0B0E14] border-[#1E293B] text-[#F8FAFC] mt-2"
              placeholder="your@email.com"
              required
            />
            {!isLogin && (
              <p className="text-[#64748B] text-xs mt-1">
                Use a valid email - you'll need to verify it
              </p>
            )}
          </div>
          
          <div>
            <Label htmlFor="password" className="text-[#F8FAFC]">Password</Label>
            <Input
              data-testid="auth-password-input"
              id="password"
              type="password"
              value={formData.password}
              onChange={handlePasswordChange}
              className="bg-[#0B0E14] border-[#1E293B] text-[#F8FAFC] mt-2"
              required
            />
            
            {/* Password Strength Indicator */}
            {!isLogin && passwordStrength && (
              <div className="mt-2">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-[#64748B]">Password strength:</span>
                  <span style={{ color: passwordStrength.color }}>{passwordStrength.level}</span>
                </div>
                <div className="h-1 bg-[#1E293B] rounded-full overflow-hidden">
                  <div 
                    className="h-full transition-all duration-300"
                    style={{ width: passwordStrength.width, backgroundColor: passwordStrength.color }}
                  />
                </div>
              </div>
            )}
            
            {/* Password Requirements */}
            {!isLogin && (
              <div className="mt-2 text-xs text-[#64748B]">
                <p className="mb-1">Password must contain:</p>
                <ul className="grid grid-cols-2 gap-1">
                  <li className={formData.password.length >= 8 ? 'text-[#10B981]' : ''}>
                    • 8+ characters
                  </li>
                  <li className={/[A-Z]/.test(formData.password) ? 'text-[#10B981]' : ''}>
                    • Uppercase letter
                  </li>
                  <li className={/[a-z]/.test(formData.password) ? 'text-[#10B981]' : ''}>
                    • Lowercase letter
                  </li>
                  <li className={/[0-9]/.test(formData.password) ? 'text-[#10B981]' : ''}>
                    • Number
                  </li>
                  <li className={/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? 'text-[#10B981]' : ''}>
                    • Special character
                  </li>
                </ul>
              </div>
            )}
          </div>
          
          <Button 
            data-testid="auth-submit-btn"
            type="submit" 
            className="w-full bg-[#00E5FF] text-black hover:bg-[#33EBFF] font-semibold py-6 rounded-md"
            disabled={loading}
          >
            {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Sign Up')}
          </Button>
        </form>
        
        <div className="mt-6 text-center">
          <button
            data-testid="auth-toggle-btn"
            onClick={() => {
              setIsLogin(!isLogin);
              setPasswordStrength(null);
            }}
            className="text-[#00E5FF] hover:text-[#33EBFF] font-medium"
          >
            {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
